import {FC, useState} from 'react'
import {MenuButton, MenuProvider, useDialogStore} from '@ariakit/react'
import {Button} from '@components/ui/button/Button.tsx'
import {
    DotsHorizontalIcon,
    File02Icon,
    Pencil01Icon,
    QueueListIcon,
    Trash01Icon,
    XCloseIcon
} from '@components/ui/icon/Icon.tsx'
import {Menu, MenuGroup, MenuItem} from '@components/ui/menu-atoms/MenuAtoms.tsx'
import {useTranslation} from '@/translations/i18n.tsx'
import {Access} from '../../types'
import {generatePath, Link} from 'react-router-dom'
import {routes} from '@/utilities/constants/routes'
import {EditAbsenceDialog} from '../edit-absence-dialog/EditAbsenceDialog'
import {EditAccessDialog} from '../edit-access-dialog/EditAccessDialog'
import {ValidateAccessButton} from './ValidateAccessButton'
import {DeleteAccessModal} from './DeleteAccessModal'
import {generateCreateMedicalReportPath} from '@/features/medical-reports/utils'
import {InvalidateAccessButton} from './InvalidateAccessButton'
import {usePermissions} from '@/features/auth/store/store'
import dayjs from 'dayjs'

export const AccessesOtherActionMenu: FC<{access: Access; onDeleteSuccess: () => void}> = ({
    access,
    onDeleteSuccess
}) => {
    const {t} = useTranslation()
    const [isDeleteAccessModalOpen, setIsDeleteAccessModalOpen] = useState(false)
    const [isOpen, setOpen] = useState(false)
    const permissions = usePermissions()
    const editAccessDialogStore = useDialogStore()
    const editAbsenceDialogStore = useDialogStore()

    const path = generateCreateMedicalReportPath(
        access.patientId,
        access.projectId,
        access.event?.eventConsultants?.[0]?.accessType?.medicalRecordReportType,
        [access.id]
    )

    return (
        <>
            <MenuProvider open={isOpen} setOpen={setOpen}>
                <MenuButton
                    render={
                        <Button
                            className={isOpen ? undefined : 'show-on-hover'}
                            size="xs"
                            shape="square"
                            variant="secondary"
                            onClick={event => event.stopPropagation()}
                        >
                            <DotsHorizontalIcon />
                        </Button>
                    }
                />
                <Menu width="220px" onClick={e => e.stopPropagation()}>
                    <MenuGroup>
                        {!(access.type === 'absence' && access.event.communicatedOnTime == null) && (
                            <>
                                {access.status == 'to_validate' ? (
                                    <ValidateAccessButton access={access} />
                                ) : (
                                    access.status == 'validated' && <InvalidateAccessButton access={access} />
                                )}
                            </>
                        )}
                        {access.status === 'accounted' && (
                            <MenuItem disabled>
                                <XCloseIcon />
                                {t('accesses:restore_to_validated')}
                            </MenuItem>
                        )}
                        {access.status === 'to_validate' && (
                            <MenuItem
                                onClick={() =>
                                    access.type === 'access'
                                        ? editAccessDialogStore.setOpen(true)
                                        : editAbsenceDialogStore.setOpen(true)
                                }
                            >
                                <Pencil01Icon />
                                {t('commons:edit')}
                            </MenuItem>
                        )}
                        {access.projectId && permissions?.projects?.read && (
                            <MenuItem
                                render={
                                    <Link
                                        to={generatePath(routes.PROJECT_DETAILS_PLANNED_ACCESSES.handle.path, {
                                            id: access.projectId
                                        })}
                                    >
                                        <QueueListIcon />
                                        {t('accesses:title_pri_accesses_absences')}
                                    </Link>
                                }
                            />
                        )}
                        {permissions?.medicalRecords.write && (
                            <>
                                {access.medicalRecordReport
                                    ? access.medicalRecordReport.status == 'draft' ||
                                      (access.medicalRecordReport.status == 'signed' &&
                                          access.medicalRecordReport.signedAt &&
                                          dayjs().diff(dayjs(access.medicalRecordReport.signedAt), 'days') <= 30 && (
                                              <MenuItem
                                                  render={
                                                      <Link
                                                          to={generatePath(routes.EDIT_MEDICAL_REPORT.handle.path, {
                                                              id: access.patientId,
                                                              medicalReportId: access.medicalRecordReport.id
                                                          })}
                                                      >
                                                          <File02Icon />
                                                          {t('accesses:edit_report')}
                                                      </Link>
                                                  }
                                              />
                                          ))
                                    : path && (
                                          <MenuItem
                                              render={
                                                  <Link to={path}>
                                                      <File02Icon />
                                                      {t('commons:add_report')}
                                                  </Link>
                                              }
                                          />
                                      )}
                            </>
                        )}
                    </MenuGroup>
                    <MenuGroup>
                        <MenuItem variant="danger" onClick={() => setIsDeleteAccessModalOpen(true)}>
                            <Trash01Icon />
                            {t('commons:delete')}
                        </MenuItem>
                    </MenuGroup>
                </Menu>
            </MenuProvider>
            <EditAccessDialog access={access} dialogStore={editAccessDialogStore} />
            <EditAbsenceDialog access={access} dialogStore={editAbsenceDialogStore} />
            {isDeleteAccessModalOpen && (
                <DeleteAccessModal
                    access={access}
                    onDeleteSuccess={onDeleteSuccess}
                    onClose={() => setIsDeleteAccessModalOpen(false)}
                />
            )}
        </>
    )
}
